import React, { useState, useEffect } from "react";
import { onAuthStateChanged, signOut, sendEmailVerification } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth } from "./firebase";
import { useAuth } from "./AuthContext";
import Modal from "./Modal";
import { 
  FiUser, 
  FiMail, 
  FiLock, 
  FiLogOut, 
  FiAlertCircle,
  FiRefreshCw,
  FiTrash2, 
  FiCheckCircle,
  FiCreditCard,
  FiUpload,
  FiInfo
} from "react-icons/fi";
import "./Profile.css";
const CONFIG = require("./config");

function Profile() {
  const [currentUser, setCurrentUser] = useState(null);
  const [quota, setQuota] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [responseMessage, setResponseMessage] = useState(null);
  const navigate = useNavigate();
  const { resetReauthenticationStatus, setNextPageAfterReauth } = useAuth();

  // Listen for authentication state changes.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const isEmailSignIn = user.providerData.some((p) => p.providerId === "password");
        setCurrentUser({
          email: user.email,
          emailVerified: user.emailVerified,
          isEmailSignIn,
        });
      } else {
        setCurrentUser(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Check custom claims to set premium status.
  useEffect(() => {
    if (currentUser) {
      auth.currentUser
        .getIdTokenResult(true)
        .then((idTokenResult) => {
          setIsPremium(!!idTokenResult.claims.premium);
        })
        .catch((err) => {
          console.error("Error checking premium status:", err);
          setIsPremium(false);
        });
    } else {
      setIsPremium(false);
    }
  }, [currentUser]);

  // Fetch quota if on the free plan.
  const fetchQuota = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(CONFIG.API_URL+"/quota", {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to fetch quota");
      const data = await response.json();
      setQuota(data);
    } catch (error) {
      console.error("Error fetching quota:", error);
    }
  };

  useEffect(() => {
    if (currentUser && !isPremium) {
      fetchQuota();
    }
  }, [currentUser, isPremium]);

  // Handlers
  const handleSignOut = () => {
    signOut(auth)
      .then(() => {
        resetReauthenticationStatus();
        navigate("/login");
      })
      .catch((error) => console.error("Sign out error:", error));
  };

  const requestEmailVerification = () => {
    if (currentUser && !currentUser.emailVerified) {
      sendEmailVerification(auth.currentUser)
        .then(() => alert("Verification email sent."))
        .catch((error) => console.error("Error sending verification email:", error));
    }
  };

  const handleChangePasswordClick = () => {
    if (currentUser.isEmailSignIn) {
      setNextPageAfterReauth("/dashboard/new-password");
      navigate("/dashboard/reauthenticate");
    }
  };

  const handleChangeEmailClick = () => {
    if (currentUser.isEmailSignIn) {
      setNextPageAfterReauth("/dashboard/change-email");
      navigate("/dashboard/reauthenticate");
    }
  };

  const handleResetAccountData = () => {
    // Open the confirmation modal
    setIsModalOpen(true);
    setResponseMessage(null);
  };

  const handleResetConfirm = async () => {
    setIsProcessing(true);
    
    try {
      const token = await auth.currentUser.getIdToken();
      
      // Use POST method which is more appropriate for data modification operations
      const response = await fetch(`${CONFIG.API_URL}/reset_account`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setResponseMessage({
          type: "success",
          text: data.message || "Your account data has been reset successfully."
        });
      } else {
        setResponseMessage({
          type: "error",
          text: data.error || "Failed to reset account data. Please try again."
        });
      }
    } catch (error) {
      console.error("Error resetting account data:", error);
      setResponseMessage({
        type: "error",
        text: "An error occurred. Please try again later."
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setResponseMessage(null);
  };

  const handleDeleteAccount = () => {
    setNextPageAfterReauth("/dashboard/delete-account-confirm");
    navigate("/dashboard/reauthenticate");
  };

  const handlePremiumSubscription = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const firebaseUid = auth.currentUser.uid;
      const response = await fetch(
        CONFIG.CREATE_CHECKOUT_SESSION,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ firebaseUid }),
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Subscription error");
      }
      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      console.error("Premium subscription error:", error);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(
        CONFIG.CREATE_BILLINGPORTAL_SESSION,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Billing portal error");
      }
      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      console.error("Error managing subscription:", error);
    }
  };

  // Progress bar for daily quota (if free)
  let progressPercent = 0;
  if (quota && quota.limit !== "unlimited") {
    progressPercent = (quota.used / quota.limit) * 100;
    if (progressPercent > 100) progressPercent = 100; // cap at 100
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>User Profile</h1>
        <p>Manage your account settings and subscription</p>
      </div>

      {currentUser ? (
        <div className="profile-cards-wrapper">
          {/* -- Plan / User Info Card -- */}
          <div className="profile-card">
            <div className="section-title">User Information</div>
            
            <div className="user-info-item">
              <FiUser />
              <strong>Email:</strong> {currentUser.email}
            </div>

            {/* Plan Info */}
            {isPremium ? (
              <>
                <div className="premium-text">
                  <FiCheckCircle />
                  <span>Premium Subscription (Higher Usage Limits)</span>
                </div>
                
                <div className="info-text">
                  <FiInfo />
                  <span>You have access to all premium features including 100 jobs per week and 200 resumes per job.</span>
                </div>
                
                <div className="form-group">
                  <button className="btn primary-btn" onClick={handleManageSubscription}>
                    <FiCreditCard /> Manage Subscription
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="freeplan-text">
                  <strong>Free Plan</strong>
                  {quota && (
                    <div>  
                      Weekly usage: {quota.used} of {quota.limit} resume rankings
                      ({quota.remaining} remaining)
                    </div>
                  )}
                </div>

                {/* Progress bar with color coding based on usage */}
                {quota && quota.limit !== "unlimited" && (
                  <div className="quota-bar-container">
                    <div
                      className={`quota-bar-fill ${
                        progressPercent > 80 ? 'quota-high' : 
                        progressPercent > 50 ? 'quota-medium' : 'quota-low'
                      }`}
                      style={{ width: `${progressPercent}%` }}
                    ></div>
                  </div>
                )}

                <div className="form-group">
                  <button className="btn upgrade-btn" onClick={handlePremiumSubscription}>
                    <FiUpload /> Upgrade to Premium
                  </button>
                </div>
              </>
            )}

            <div className="form-group">
              <button className="btn secondary-btn" onClick={handleSignOut}>
                <FiLogOut /> Sign Out
              </button>
            </div>
          </div>

          {/* -- Email & Password Settings (if email sign-in) -- */}
          {currentUser && currentUser.isEmailSignIn && (
            <div className="profile-card">
              <div className="section-title">Security & Email</div>

              {/* Email verified status + resend */}
              {!currentUser.emailVerified && (
                <div className="warning-text">
                  <FiAlertCircle />
                  <span>Your email is not verified. Please verify your email for account security.</span>
                </div>
              )}
              
              {!currentUser.emailVerified && (
                <div className="form-group">
                  <button className="btn secondary-btn" onClick={requestEmailVerification}>
                    <FiMail /> Resend Verification Email
                  </button>
                </div>
              )}
              
              <div className="form-group">
                <button className="btn secondary-btn" onClick={handleChangeEmailClick}>
                  <FiMail /> Change Email
                </button>
              </div>

              {/* Password */}
              <div className="form-group">
                <button className="btn secondary-btn" onClick={handleChangePasswordClick}>
                  <FiLock /> Change Password
                </button>
              </div>
            </div>
          )}

          {/* -- Account Data Card (Reset/Delete) -- */}
          <div className="profile-card">
            <div className="section-title">Account Data</div>
            
            <div className="warning-text">
              <FiAlertCircle />
              <span>The following actions will affect your stored data and cannot be undone.</span>
            </div>
            
            <div className="form-group">
              <button className="btn danger-btn" onClick={handleResetAccountData}>
                <FiRefreshCw /> Reset Account Data
              </button>
            </div>
            <div className="form-group">
              <button className="btn danger-btn" onClick={handleDeleteAccount}>
                <FiTrash2 /> Delete Account
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="profile-card">
          <div className="warning-text">
            <FiAlertCircle />
            <span>No user is logged in. Please log in to view your profile.</span>
          </div>
        </div>
      )}

      {/* Reset Account Confirmation Modal */}
      <Modal
        isOpen={isModalOpen}
        title="Reset Account Data"
        message="Are you sure you want to reset your account data? All your job descriptions and CVs will be removed. This action cannot be undone."
        onClose={handleCloseModal}
        onConfirm={handleResetConfirm}
        isProcessing={isProcessing}
        responseMessage={responseMessage}
      />
    </div>
  );
}

export default Profile;